import { RoutineIdpStatus } from '../../../graphql';
import { RoutineState } from '../routineState';
import {
  WorkflowResponseState,
  WorkflowAuditLog,
} from './workflowResponseState';

export interface WorkflowRoutineState extends RoutineState {
  responses: WorkflowResponseState[];
  auditLog?: WorkflowAuditLog;
  idp?: RoutineIdpState;
}

/**
 * `RoutineIdpStatus` is codegen'd from the stitched schema into `graphql/enums.ts`
 * — the Pothos `applyRoutineIdpResult` mutation declares it, so it is a real
 * GraphQL enum despite the value living inside freeform `AssetRoutine.data` JSON.
 * Re-exported here so consumers of this state shape get it from one import.
 */
export { RoutineIdpStatus };

export interface RoutineIdpState {
  status: RoutineIdpStatus;
  error?: string;
  completedAt?: Date;
}

/**
 * The two statuses extraction can still move out of. Everything else —
 * `PROCESSED`, `FAILED`, or no `idp` state at all — is terminal: values are
 * final, the edit lock is released, and polling stops.
 */
const NON_TERMINAL_IDP_STATUSES: ReadonlySet<RoutineIdpStatus> = new Set([
  RoutineIdpStatus.Pending,
  RoutineIdpStatus.Processing,
]);

/**
 * Whether extraction is still in flight. Kept here rather than in the poll
 * hook because the render side (per-field processing state, the edit lock's
 * UI affordance) has to draw the same line the poll gate does — a second copy
 * of this set is how a status added later ends up terminal in one place and
 * non-terminal in the other.
 *
 * `undefined` (a non-IDP routine, or a response that has not resolved yet) is
 * treated as terminal: nothing is waiting, so nothing should block or spin.
 */
export const isNonTerminalIdpStatus = (
  status: RoutineIdpStatus | undefined
): boolean => status !== undefined && NON_TERMINAL_IDP_STATUSES.has(status);
